import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory } from 'react-router-dom';
import {
  AdminCategory,
  ApiError,
  ItemLookupKind,
  ItemLookupResponse,
  ItemSearchHit,
  ItemTenant,
  listCategories,
  listItemTenants,
  lookupItems,
} from '../../../api';
import { useAuth } from '../../../auth/AuthContext';
import ItemEditDialog from '../../dialogs/ItemEditDialog';
import Price from '../Price';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;

// Human label per match-kind. Doubles as the chip text shown next to
// each result so the operator sees *why* a row matched.
const KIND_LABELS: Record<Exclude<ItemLookupKind, 'none'>, string> = {
  sku: 'SKU',
  upc: 'UPC',
  name: 'name',
};

const KIND_COLORS: Record<
  Exclude<ItemLookupKind, 'none'>,
  'success' | 'info' | 'default'
> = {
  sku: 'success',
  upc: 'success',
  name: 'default',
};

type Props = {
  /** Pre-populates the search field. Useful on the search-results page,
   *  which reads `?q=...` from the URL and wants to mirror that into
   *  the bar so an operator can refine without re-typing from scratch. */
  initialQuery?: string;
};

const ItemSearchBar: React.FC<Props> = ({ initialQuery }) => {
  const { idToken } = useAuth();
  const history = useHistory();
  const [query, setQuery] = useState(initialQuery ?? '');
  const [response, setResponse] = useState<ItemLookupResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [popperOpen, setPopperOpen] = useState(false);
  // Tenant metadata for the chip + the edit dialog's imageRoot/skuPrefix
  // props. Cheap fetch; same call the TenantsPanel makes.
  const [tenants, setTenants] = useState<ItemTenant[]>([]);
  // Per-tenant category cache. Populated lazily when an operator opens
  // a result from that tenant — the edit dialog needs categories at
  // render time to populate its Category dropdown.
  const [catsByTenant, setCatsByTenant] = useState<
    Record<string, AdminCategory[]>
  >({});
  // Hit currently being opened in the edit dialog. The dialog renders
  // only once categories for `pickedHit.tenant` have been loaded — until
  // then we keep the result list visible with a small spinner.
  const [pickedHit, setPickedHit] = useState<ItemSearchHit | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tenants — one shot at mount. Failure is silent (no chips, falls
  // back to the slug) rather than blocking the whole search.
  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    listItemTenants(idToken)
      .then((rows) => {
        if (!cancelled) setTenants(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  // Keep the bar in sync with `initialQuery` as it changes — the
  // search-results page swaps `?q=...` on each Enter, and we want the
  // input to follow without the operator having to retype.
  useEffect(() => {
    if (initialQuery !== undefined) setQuery(initialQuery);
  }, [initialQuery]);

  // Debounced lookup. Below the min-length we still close the popper so
  // a stale dropdown doesn't linger after the operator backspaces.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LEN) {
      setResponse(null);
      setLoading(false);
      setError('');
      setPopperOpen(false);
      return;
    }
    if (!idToken) return;
    setLoading(true);
    setError('');
    timerRef.current = setTimeout(async () => {
      try {
        const r = await lookupItems(idToken, trimmed);
        setResponse(r);
        setPopperOpen(true);
      } catch (e) {
        setError(
          e instanceof ApiError
            ? `API error ${e.status}`
            : (e as Error)?.message || 'lookup failed',
        );
        setResponse(null);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, idToken]);

  // Fetch categories for the picked hit's tenant on demand. Cached per
  // tenant so opening multiple results in a row from the same tenant is
  // a single request total.
  useEffect(() => {
    if (!pickedHit || !idToken) return;
    const tenant = pickedHit.tenant;
    if (catsByTenant[tenant] !== undefined) return;
    let cancelled = false;
    listCategories(idToken, tenant)
      .then((cats) => {
        if (!cancelled) {
          setCatsByTenant((prev) => ({ ...prev, [tenant]: cats }));
        }
      })
      .catch(() => {
        // Still set an empty array so the dialog can render — the
        // operator can manually pick a category if Mongo's category
        // list briefly fails. Better than a stuck "loading" state.
        if (!cancelled) {
          setCatsByTenant((prev) => ({ ...prev, [tenant]: [] }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [pickedHit, idToken, catsByTenant]);

  const tenantEntry = useMemo(
    () =>
      pickedHit ? tenants.find((t) => t.slug === pickedHit.tenant) : undefined,
    [pickedHit, tenants],
  );
  const categoriesReady =
    pickedHit !== null && catsByTenant[pickedHit.tenant] !== undefined;

  const handlePick = (hit: ItemSearchHit) => {
    setPickedHit(hit);
    setPopperOpen(false);
  };

  const handleClear = () => {
    setQuery('');
    setResponse(null);
    setPopperOpen(false);
    setError('');
  };

  const handleClickAway = () => {
    setPopperOpen(false);
  };

  // Enter: jump to the dedicated search-results page. URL carries the
  // query as `?q=...` so the result view is shareable / bookmarkable
  // (operators can DM a link to a specific search). We close the
  // popper because the page renders its own result grid and the
  // hovering dropdown would just duplicate it.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    const trimmed = query.trim();
    if (trimmed === '') return;
    setPopperOpen(false);
    history.push(`/items/search?q=${encodeURIComponent(trimmed)}`);
  };

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box ref={anchorRef} sx={{ position: 'relative', width: '100%' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search items by SKU, UPC, name… (Enter for a shareable results page)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (response) setPopperOpen(true);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8' }} fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? (
                    <CircularProgress size={16} />
                  ) : query !== '' ? (
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      sx={{ p: 0.25 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />
          <Popper
            open={popperOpen && (response !== null || error !== '')}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            // Width-match the field. The Popper inherits no width from
            // the anchor by default, so without this it renders as
            // wide as the content (often jarring).
            sx={{
              width: anchorRef.current?.clientWidth ?? 'auto',
              zIndex: (t) => t.zIndex.modal,
            }}
          >
            <Paper
              elevation={4}
              sx={{ mt: 0.5, maxHeight: 360, overflow: 'auto' }}
            >
              {error && (
                <Alert severity="error" sx={{ m: 1 }}>
                  {error}
                </Alert>
              )}
              {response && (
                <LookupResultsList
                  response={response}
                  tenants={tenants}
                  onPick={handlePick}
                />
              )}
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>

      {/* When the operator picks a hit but we haven't loaded categories
          yet, show a tiny spinner near the field so they know something
          is happening. The dialog opens the moment categories arrive. */}
      {pickedHit !== null && !categoriesReady && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
          <CircularProgress size={16} />
        </Box>
      )}

      <ItemEditDialog
        open={pickedHit !== null && categoriesReady}
        tenant={pickedHit?.tenant ?? ''}
        imageRoot={tenantEntry?.image_root ?? ''}
        skuPrefix={tenantEntry?.sku_prefix ?? ''}
        itemId={pickedHit?.id ?? null}
        categories={
          pickedHit ? (catsByTenant[pickedHit.tenant] ?? []) : []
        }
        onClose={() => setPickedHit(null)}
        // No reload tick to bump — the search bar isn't tied to a
        // particular item grid; if the operator wants to re-search
        // after editing, they can type again.
        onSaved={() => {}}
      />
    </>
  );
};

const LookupResultsList: React.FC<{
  response: ItemLookupResponse;
  tenants: ItemTenant[];
  onPick: (hit: ItemSearchHit) => void;
}> = ({ response, tenants, onPick }) => {
  if (response.kind === 'none' || response.hits.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: '#94a3b8',
          fontStyle: 'italic',
          textAlign: 'center',
          p: 2,
        }}
      >
        No matches.
      </Typography>
    );
  }
  const kindLabel = KIND_LABELS[response.kind];
  const kindColor = KIND_COLORS[response.kind];

  return (
    <List dense disablePadding>
      {response.hits.map((hit) => {
        const tenantLabel =
          tenants.find((t) => t.slug === hit.tenant)?.label ?? hit.tenant;
        return (
          <ListItemButton
            key={`${hit.tenant}-${hit.id}`}
            onClick={() => onPick(hit)}
            sx={{ alignItems: 'flex-start', py: 1 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mb: 0.25,
                }}
              >
                <Chip
                  label={kindLabel}
                  size="small"
                  color={kindColor}
                  variant={kindColor === 'default' ? 'outlined' : 'filled'}
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {hit.brand ? `${hit.brand} — ` : ''}
                  {hit.name}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{ color: '#94a3b8', fontFamily: 'monospace' }}
              >
                {hit.id}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 0.5,
                ml: 1,
              }}
            >
              <Chip label={tenantLabel} size="small" variant="outlined" />
              <Typography variant="body2">
                <Price price={hit.price ?? 0} showZero />
              </Typography>
            </Box>
          </ListItemButton>
        );
      })}
    </List>
  );
};

export default ItemSearchBar;
